import { useState, useEffect } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { programService } from "@/services/program.service";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Package, Settings, Link2, Hash, Users, Percent, DollarSign, Loader, HelpCircle } from "react-feather";
import PageHeader from "@/components/common/PageHeader";
import CreateProgram from "./CreateProgram";
import { paymentMethodsService, BrandSupportedPaymentMethod } from "@/services/paymentMethods.service";
import { BrandPayoutMethodsEditor } from "@/components/brand/payouts/BrandPayoutMethodsEditor";
import { BrandContext } from "@/contexts/BrandContext/context";
import { useContext } from "react";
import toast from "react-hot-toast";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { CopyProgramLinkButton } from "@/components/common/CopyProgramLinkButton";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { UnderlineTabs } from "@/components/ui/underline-tabs";

interface Program {
  id: string;
  commissionValue: string;
  commissionType?: "PERCENTAGE" | "FIXED";
  discountValue: string;
  joinTitle?: string;
  isDefault: boolean;
  status: "ACTIVE" | "INACTIVE" | "SUSPENDED";
  createdAt?: string;
  updatedAt?: string;
  attributionMethod?: "STATIC_CODE" | "SNEAKYLINK";
  name?: string;
  // Opaque handle for this program's shareable join link (`?p=<shareToken>`).
  shareToken?: string;
}

/** How a program is named in the table and in copy-link toasts. */
function programLabel(program: Program) {
  if (program.isDefault && (!program.name || program.name === "Default Program")) {
    return "Discoverable Program";
  }
  return program.name || "Custom Program";
}

// The name column sizes to its content (the table is min-w-max and scrolls),
// so absurdly long names get a hard cap instead of stretching the table.
function clampLabel(label: string, max = 100) {
  return label.length > max ? `${label.slice(0, max).trimEnd()}…` : label;
}

interface ProgramTableRowProps {
  program: Program;
  onEdit: (id: string) => void;
  affiliateCount?: number;
  onSetDefault: (programId: string) => void;
  loadingProgramId: string | null;
  brandUsername?: string;
}

function ProgramTableRow({ program, onEdit, affiliateCount, onSetDefault, loadingProgramId, brandUsername }: ProgramTableRowProps) {
  const isSuspended = program.status === 'SUSPENDED';
  const isSneakylink = program.attributionMethod === 'SNEAKYLINK';
  const isLoading = loadingProgramId === program.id;

  const handleRowClick = () => {
    onEdit(program.id);
  };

  const handleToggleDefault = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!program.isDefault && !isLoading && !loadingProgramId) {
      onSetDefault(program.id);
    }
  };

  return (
    <TableRow
      onClick={handleRowClick}
      className={`cursor-pointer ${isSuspended ? 'opacity-75' : ''}`}
    >
      {/* Program Name */}
      <TableCell>
        <div className="flex items-center gap-2">
          <span
            className="whitespace-nowrap font-semibold text-foreground"
            title={programLabel(program)}
          >
            {clampLabel(programLabel(program))}
          </span>
        </div>
      </TableCell>

      {/* Commission */}
      <TableCell>
        <div className="flex items-center gap-1.5 text-sm">
          <DollarSign className="w-4 h-4 text-brand-600" />
          <span className="text-muted-foreground font-medium">
            {Number(program.commissionValue)}
            {program.commissionType === "FIXED" ? " USD" : "%"}
          </span>
        </div>
      </TableCell>

      {/* Discount */}
      <TableCell>
        <div className="flex items-center gap-1.5 text-sm">
          <Percent className="w-4 h-4 text-brand-600" />
          <span className="text-muted-foreground font-medium">
            {Number(program.discountValue)}%
          </span>
        </div>
      </TableCell>

      {/* Code Type & Tags */}
      <TableCell>
        <div className="flex flex-col gap-2">
          {isSneakylink ? (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand-50 border border-brand-200 rounded-lg w-fit">
              <Link2 className="w-3.5 h-3.5 text-brand-600" />
              <span className="text-xs font-medium text-brand-700">Sneakylink</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-muted border border-border rounded-lg w-fit">
              <Hash className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Multi-use Code</span>
            </div>
          )}
          {isSuspended && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg w-fit">
              <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Suspended</span>
            </div>
          )}
        </div>
      </TableCell>

      {/* Affiliates */}
      <TableCell>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="w-4 h-4" />
          <span className="font-medium">
            {affiliateCount !== undefined ? affiliateCount : '-'}
          </span>
        </div>
      </TableCell>

      {/* Join Link — one shareable link per program */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <CopyProgramLinkButton
          username={brandUsername}
          shareToken={program.shareToken}
          programLabel={programLabel(program)}
        />
      </TableCell>

      {/* Set as Default Toggle */}
      <TableCell onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleDefault}
            disabled={program.isDefault || loadingProgramId !== null}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
              program.isDefault
                ? 'bg-brand-600 cursor-default'
                : loadingProgramId !== null
                ? 'bg-muted cursor-not-allowed'
                : 'bg-muted hover:bg-muted-foreground/30 cursor-pointer'
            }`}
          >
            {isLoading ? (
              <Loader className="w-4 h-4 text-brand-600 animate-spin mx-auto" />
            ) : (
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${
                  program.isDefault ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            )}
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function DefaultProgramHeaderPopover() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex items-center gap-2">
      Discoverable
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button 
            className="text-muted-foreground hover:text-muted-foreground transition-colors"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent 
          align="end" 
          className="w-80"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">Discoverable Program</h4>
            <p className="text-sm text-muted-foreground">
              The discoverable program is the one affiliates join when they find your brand on
              Reveshare — it's automatically applied to everyone who joins on their own.
              To assign an affiliate to a different program, you can change it within the specific
              program you want them to join.
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ProgramsEmptyState({
  onCreateProgram,
}: {
  onCreateProgram: () => void;
}) {
  return (
    <EmptyState
      icon={<Package />}
      title="No programs yet"
      body="Create your first program to set up commission rates and discounts"
      action={
        <Button onClick={onCreateProgram}>
          <Package className="w-4 h-4" />
          Create your first program
        </Button>
      }
    />
  );
}

export default function ProgramsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { brandDetails } = useContext(BrandContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"active" | "create">("active");
  const [loadingProgramId, setLoadingProgramId] = useState<string | null>(null);

  const { data: programs, isLoading, isError, isFetching } = useQuery({
    queryKey: ["my-programs"],
    queryFn: () => programService.getMyPrograms(),
  });

  // Fetch affiliate counts for all programs in a single request
  const { data: affiliateCounts } = useQuery({
    queryKey: ["program-affiliate-counts"],
    queryFn: () => programService.getAffiliateCountsForPrograms(),
    enabled: !!programs && programs.length > 0,
  });

  // Fetch brand supported payment methods (shared across all programs)
  const {
    data: paymentMethods = [],
    refetch: refetchPaymentMethods,
  } = useQuery({
    queryKey: ["brandSupportedPaymentMethods", brandDetails?.id],
    queryFn: () =>
      brandDetails?.id
        ? paymentMethodsService.getBrandSupportedPaymentMethods(brandDetails.id)
        : Promise.resolve([]),
    enabled: !!brandDetails?.id,
  });

  // State for editing payment methods
  const [isEditingPaymentMethods, setIsEditingPaymentMethods] = useState(false);

  // Set default program mutation
  const { mutate: setDefaultProgram } = useMutation({
    mutationFn: (programId: string) => {
      setLoadingProgramId(programId);
      return programService.setDefaultProgram(programId);
    },
    onSuccess: () => {
      toast.success("Discoverable program updated successfully");
      queryClient.invalidateQueries({ queryKey: ["my-programs"] });
      setLoadingProgramId(null);
    },
    onError: (err: unknown) => {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Failed to update discoverable program";
      toast.error(errorMessage);
      setLoadingProgramId(null);
    },
  });

  const handleEditPaymentMethods = () => {
    setIsEditingPaymentMethods(true);
  };

  // Initialize tab from URL search params
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "create" || tabParam === "active") {
      setActiveTab(tabParam);
    } else {
      setActiveTab("active");
    }
  }, [searchParams]);

  // Update URL when tab changes
  const updateTab = (tab: "active" | "create") => {
    setActiveTab(tab);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", tab);
    setSearchParams(newSearchParams);
  };

  const handleCreateProgram = () => {
    updateTab("create");
  };

  const handleEditProgram = (programId: string) => {
    navigate(`/brand/programs/${programId}`);
  };

  const handleProgramSaved = (programId?: string) => {
    // Refetch programs
    queryClient.invalidateQueries({ queryKey: ["my-programs"] });
    // Navigate to program settings page if programId is provided, otherwise switch to active tab
    if (programId) {
      navigate(`/brand/programs/${programId}`);
    } else {
      updateTab("active");
    }
  };

  const handleRetry = () => {
    // Refetch programs
    queryClient.invalidateQueries({ queryKey: ["my-programs"] });
  };

  const renderActiveTab = () => {
    if (isLoading || isFetching) {
      return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <Table className="min-w-max">
                <TableHeader>
                  <TableRow>
                    <TableHead>Program</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Code Type</TableHead>
                    <TableHead>Affiliates</TableHead>
                    <TableHead>Join link</TableHead>
                    <TableHead>
                      <DefaultProgramHeaderPopover />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="h-5 bg-muted rounded w-32 animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-muted rounded w-16 animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-muted rounded w-12 animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-6 bg-muted rounded w-24 animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-6 bg-muted rounded w-24 animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-6 bg-muted rounded w-20 animate-pulse" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="bg-card rounded-lg border border-border p-6 animate-pulse">
              <div className="h-5 bg-muted rounded w-3/4 mb-4" />
              <div className="space-y-2">
                <div className="h-4 bg-muted rounded w-full" />
                <div className="h-4 bg-muted rounded w-full" />
                <div className="h-4 bg-muted rounded w-2/3" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isError) {
      return (
        <EmptyState
          icon={<Package />}
          title="Failed to load programs"
          body="There was an error loading your programs"
          action={<Button onClick={handleRetry}>Try again</Button>}
        />
      );
    }

    const sortedPrograms = programs
      ? [...(programs as Program[])].sort((a: Program, b: Program) => {
          // Sort so default program appears first
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return 0;
        })
      : [];

    if (sortedPrograms.length === 0) {
      return <ProgramsEmptyState onCreateProgram={handleCreateProgram} />;
    }

    const paymentMethodNames = (paymentMethods as BrandSupportedPaymentMethod[]).map(
      (pm) => pm.paymentMethod.name
    );

    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Programs Table */}
        <div className="lg:col-span-2">
          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <Table className="min-w-max">
              <TableHeader>
                <TableRow>
                  <TableHead>Program</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Code Type</TableHead>
                  <TableHead>Affiliates</TableHead>
                  <TableHead>Join link</TableHead>
                  <TableHead>
                    <DefaultProgramHeaderPopover />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPrograms.map((program: Program) => (
                  <ProgramTableRow
                    key={program.id}
                    program={program}
                    onEdit={handleEditProgram}
                    affiliateCount={affiliateCounts?.[program.id]}
                    onSetDefault={setDefaultProgram}
                    loadingProgramId={loadingProgramId}
                    brandUsername={brandDetails?.username}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Payment Methods Card - Right Side */}
        <div className="lg:col-span-1">
          <div className="bg-card rounded-lg border border-border p-6 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-foreground">
                Supported Payment Methods
              </h3>
              {!isEditingPaymentMethods && (
                <button
                  onClick={handleEditPaymentMethods}
                  className="text-brand-600 hover:text-brand-700 text-sm flex items-center gap-1"
                  title="Edit payment methods"
                >
                  <Settings className="w-4 h-4" />
                </button>
              )}
            </div>

            {!isEditingPaymentMethods ? (
              <div className="flex flex-wrap gap-2">
                {paymentMethodNames.length > 0 ? (
                  paymentMethodNames.map((name, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs font-medium bg-muted text-muted-foreground rounded"
                    >
                      {name}
                    </span>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">
                    Not chosen yet. You'll be asked when an affiliate's first payout is ready.
                  </span>
                )}
              </div>
            ) : brandDetails?.id ? (
              <BrandPayoutMethodsEditor
                brandDetailId={brandDetails.id}
                onCancel={() => setIsEditingPaymentMethods(false)}
                onSaved={() => {
                  setIsEditingPaymentMethods(false);
                  refetchPaymentMethods();
                }}
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Affiliate Programs" />

      <UnderlineTabs
        tabs={[
          { value: "active", label: "Active programs", id: "active-programs" },
          { value: "create", label: "Create a program", id: "create-program" },
        ]}
        value={activeTab}
        onValueChange={updateTab}
        accent="brand"
      />

      {activeTab === "active" && <div className="space-y-6">{renderActiveTab()}</div>}

      {activeTab === "create" && (
        <div className="max-w-[75%]">
          <CreateProgramWrapper 
            onProgramSaved={handleProgramSaved}
            onCancel={() => updateTab("active")}
          />
        </div>
      )}
    </div>
  );
}

// Wrapper component to handle program creation with callback
function CreateProgramWrapper({ 
  onProgramSaved, 
  onCancel 
}: { 
  onProgramSaved: (programId?: string) => void;
  onCancel: () => void;
}) {
  return <CreateProgram onSuccess={onProgramSaved} onCancel={onCancel} />;
}
